import {normalizeWorkforceProfile} from '../WorkforceProfiles/normalizeWorkforceProfile';
import type {ApiWorkforceProfileRecord} from '../WorkforceProfiles/workforceProfileApiTypes';

import {mapTargetProfileAlignmentAssessmentScope} from './mapAssessmentScope';
import {mapCareerCoachAnalysis} from './mapCareerCoachAnalysis';
import {
  buildCoreCompetencyRows,
  buildDomainKnowledgeRows,
  buildHardSkillRows,
  buildResponsibilitiesRows,
  buildSoftSkillRows,
  buildToolsRows,
} from './mapCompetencyRows';
import {mapCulturalFitAnalysis} from './mapCulturalFitAnalysis';
import {mapProfileAnalysisDataInput} from './mapExtractedProfile';
import {mapHighlights} from './mapHighlights';
import {mapSeniorityLevelAssessment} from './mapSeniorityLevelAssessment';
import {mapVersionEvolution} from './mapVersionEvolution';
import type {CompanyWorkforceProfileDetail} from '../../types/WorkforceProfileTypes';

export const normalizeWorkforceProfileDetail = (raw: ApiWorkforceProfileRecord): CompanyWorkforceProfileDetail => {
  const analysis = raw.targetProfileAlignmentAnalysis as ApiWorkforceProfileRecord | undefined;
  const digest = analysis?.alignmentDigest as ApiWorkforceProfileRecord | undefined;
  const base = normalizeWorkforceProfile(raw);
  const culturalFitAnalysis = mapCulturalFitAnalysis(raw.culturalFitAnalysis as ApiWorkforceProfileRecord | undefined);
  const highlights = mapHighlights(digest);
  const summary =
    (raw.targetProfileAlignmentSummary as string | undefined) ?? (analysis?.summary as string | undefined) ?? null;

  return {
    ...base,
    targetProfileAlignmentAssessmentScope: mapTargetProfileAlignmentAssessmentScope(raw),
    cultureType: culturalFitAnalysis?.cultureType ?? base.cultureType,
    summary,
    highlights,
    coreCompetencyRows: buildCoreCompetencyRows(analysis),
    hardSkillRows: buildHardSkillRows(analysis),
    toolRows: buildToolsRows(analysis),
    domainKnowledgeRows: buildDomainKnowledgeRows(analysis),
    softSkillRows: buildSoftSkillRows(analysis),
    responsibilitiesRows: buildResponsibilitiesRows(analysis),
    responsibilitiesExplanation:
      (
        (analysis?.responsibilitiesAlignment as ApiWorkforceProfileRecord | undefined)?.explanation as
          | string
          | undefined
      )?.trim() || null,
    seniorityLevelAssessment: mapSeniorityLevelAssessment(
      (raw.seniorityLevelAssessment as ApiWorkforceProfileRecord | undefined) ??
        (analysis?.seniorityLevelAssessment as ApiWorkforceProfileRecord | undefined)
    ),
    processingError: (raw.processingError as string | null | undefined) ?? null,
    profileAnalysisDataInput: mapProfileAnalysisDataInput(
      raw.profileAnalysisDataInput as ApiWorkforceProfileRecord | undefined
    ),
    culturalFitAnalysis,
    careerCoachAnalysis: mapCareerCoachAnalysis(raw.careerCoachAnalysis as ApiWorkforceProfileRecord | undefined),
    versionEvolution: mapVersionEvolution(digest?.versionEvolution ?? raw.versionEvolution),
  };
};
